/* ==========================================================================
   flux-keuzehulp — wizard als custom element.

   Vragen en routeregels komen uit flux-keuzehulp-config.js. De opmaak komt
   uit Liquid: keuzekaarten, knoppen en uitkomsten staan server-side in de
   sectie (ui-radio-card, ui-button, ui-progress). Dit bestand vult alleen in
   en schakelt tussen stappen. Zo staat elke klassenstring op één plek.

   De logica bovenaan raakt de DOM niet aan, zodat hij in Node te testen is:
   node scripts/test-keuzehulp.cjs
   ========================================================================== */

(() => {
  /* ---------------------------------------------------------------- logica */

  /** Matcht een `when`/`showIf`: elke vraag moet een van de waarden hebben. */
  function matcht(voorwaarde, antwoorden) {
    return Object.entries(voorwaarde).every(([vraag, waarden]) => {
      const antwoord = antwoorden[vraag];
      return Array.isArray(antwoord)
        ? antwoord.some((v) => waarden.includes(v))
        : waarden.includes(antwoord);
    });
  }

  function zichtbareStappen(config, antwoorden) {
    return config.steps.filter((stap) => !stap.showIf || matcht(stap.showIf, antwoorden));
  }

  /**
   * Gooit antwoorden weg van stappen die niet meer zichtbaar zijn. Zonder dit
   * blijft een oud antwoord meetellen: wie eerst "met installatie" en "meer dan
   * 25 meter" kiest en daarna "alleen het product", zou nog steeds naar advies
   * gestuurd worden om een vraag die hij niet meer ziet.
   */
  function opgeschoond(config, antwoorden) {
    const zichtbaar = new Set(zichtbareStappen(config, antwoorden).map((s) => s.id));
    return Object.fromEntries(Object.entries(antwoorden).filter(([id]) => zichtbaar.has(id)));
  }

  function bepaalRoute(config, antwoorden) {
    const schoon = opgeschoond(config, antwoorden);
    const regel = config.rules.find((r) => matcht(r.when, schoon)) || null;
    return { route: regel ? regel.route : config.defaultRoute, regel };
  }

  /** Catalogus-URL met Shopify-tagfilters. Onbekende tags negeert Shopify. */
  function catalogusUrl(config, antwoorden, basis) {
    const schoon = opgeschoond(config, antwoorden);
    const tags = new Set();
    for (const [vraag, kaart] of Object.entries(config.productFilters.tagMap)) {
      [].concat(schoon[vraag] ?? []).forEach((waarde) => kaart[waarde] && tags.add(kaart[waarde]));
    }
    const params = [...tags].map((t) => `filter.p.tag=${encodeURIComponent(t)}`).join('&');
    if (!params) return basis;
    return basis + (basis.includes('?') ? '&' : '?') + params;
  }

  function isBeantwoord(stap, antwoorden) {
    if (stap.optional) return true;
    const antwoord = antwoorden[stap.id];
    return Array.isArray(antwoord) ? antwoord.length > 0 : antwoord != null && antwoord !== '';
  }

  function tekstGeldig(stap, waarde) {
    if (stap.type !== 'text' || !waarde || !stap.field?.pattern) return true;
    return new RegExp(stap.field.pattern).test(waarde);
  }

  const logica = { matcht, zichtbareStappen, opgeschoond, bepaalRoute, catalogusUrl, isBeantwoord, tekstGeldig };

  if (typeof module !== 'undefined') module.exports = logica;
  if (typeof customElements === 'undefined' || customElements.get('flux-keuzehulp')) return;

  /* ------------------------------------------------------------ component */

  const OPSLAG = 'flux-keuzehulp';

  class FluxKeuzehulp extends HTMLElement {
    connectedCallback() {
      this.config = window.FLUX_KEUZEHULP;
      if (!this.config) return;

      this.teksten = JSON.parse(this.deel('teksten').textContent);
      this.herstel();

      const formulier = this.deel('formulier');
      formulier.addEventListener('change', () => this.leesAntwoord());
      formulier.addEventListener('input', () => this.leesAntwoord());
      formulier.addEventListener('submit', (event) => {
        event.preventDefault();
        this.volgende();
      });
      this.deel('terug').addEventListener('click', () => this.terug());
      this.deel('naar-advies').addEventListener('click', () => this.naarFase('resultaat'));
      this.querySelectorAll('[data-kh="opnieuw"]').forEach((knop) =>
        knop.addEventListener('click', () => this.opnieuw())
      );
      this.deel('lijst').addEventListener('click', (event) => {
        const knop = event.target.closest('[data-index]');
        if (knop) this.naarFase('vragen', Number(knop.dataset.index));
      });

      this.deel('app').hidden = false;
      this.render(false);

      // Laat het laadscherm wegfaden (zie section-flux-keuzehulp.css).
      this.dataset.staat = 'klaar';
    }

    deel(naam) {
      return this.querySelector(`[data-kh="${naam}"]`);
    }

    sjabloon(naam) {
      return this.querySelector(`template[data-kh-sjabloon="${naam}"]`).content.firstElementChild.cloneNode(true);
    }

    get stappen() {
      return zichtbareStappen(this.config, this.antwoorden);
    }

    /* -------------------------------------------------------------- staat */

    herstel() {
      let bewaard = {};
      try {
        bewaard = JSON.parse(sessionStorage.getItem(OPSLAG)) || {};
      } catch {
        /* privémodus of geblokkeerde opslag: gewoon opnieuw beginnen */
      }
      this.antwoorden = bewaard.antwoorden || {};
      this.index = bewaard.index || 0;
      this.fase = bewaard.fase || 'vragen';
    }

    bewaar() {
      try {
        sessionStorage.setItem(
          OPSLAG,
          JSON.stringify({ antwoorden: this.antwoorden, index: this.index, fase: this.fase })
        );
      } catch {
        /* zie herstel() */
      }
    }

    leesAntwoord() {
      const stap = this.stappen[this.index];
      const formulier = this.deel('formulier');
      if (stap.type === 'text') {
        this.antwoorden[stap.id] = this.deel('tekst-invoer').value.trim();
      } else if (stap.type === 'multi') {
        this.antwoorden[stap.id] = [...formulier.querySelectorAll('input:checked')].map((i) => i.value);
      } else {
        this.antwoorden[stap.id] = formulier.querySelector('input:checked')?.value;
      }
      this.toonFout('');
      this.bewaar();
    }

    /* ---------------------------------------------------------- navigatie */

    volgende() {
      const stap = this.stappen[this.index];
      if (!isBeantwoord(stap, this.antwoorden)) return this.toonFout(this.teksten.kies_optie);
      if (!tekstGeldig(stap, this.antwoorden[stap.id])) return this.toonFout(stap.field.invalidMessage);

      this.antwoorden = opgeschoond(this.config, this.antwoorden);
      if (this.index < this.stappen.length - 1) {
        this.naarFase('vragen', this.index + 1);
      } else {
        this.naarFase('overzicht');
      }
    }

    terug() {
      if (this.index > 0) this.naarFase('vragen', this.index - 1);
    }

    naarFase(fase, index = this.index) {
      this.fase = fase;
      this.index = Math.max(0, Math.min(index, this.stappen.length - 1));
      this.bewaar();
      this.render(true);
    }

    opnieuw() {
      this.antwoorden = {};
      this.naarFase('vragen', 0);
    }

    /* ------------------------------------------------------------- render */

    render(verplaatsFocus) {
      for (const fase of ['vragen', 'overzicht', 'resultaat']) {
        this.deel(fase).hidden = this.fase !== fase;
      }

      if (this.fase === 'vragen') this.renderStap();
      if (this.fase === 'overzicht') this.renderOverzicht();
      if (this.fase === 'resultaat') this.renderResultaat();

      this.renderVoortgang();
      this.renderPaneel();
      this.speelAnimatie();

      if (verplaatsFocus) {
        // offsetParent is null zolang een voorouder verborgen is: zo pakken we
        // in het resultaat de kop van de route die echt getoond wordt.
        [...this.deel(this.fase).querySelectorAll('[data-kh-kop]')]
          .find((kop) => kop.offsetParent !== null)
          ?.focus({ preventScroll: true });
        if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
      }
    }

    renderStap() {
      const stap = this.stappen[this.index];
      this.deel('titel').textContent = stap.title;
      this.deel('intro').textContent = stap.intro || '';
      this.deel('intro').hidden = !stap.intro;
      this.deel('hulp').hidden = !stap.help;
      this.deel('hulp').open = false;
      this.deel('hulp-tekst').textContent = stap.help || '';

      const opties = this.deel('opties');
      opties.replaceChildren(...(stap.options || []).map((optie) => this.maakOptie(stap, optie)));
      opties.hidden = stap.type === 'text';
      opties.dataset.layout = stap.layout || 'list';

      this.deel('tekst').hidden = stap.type !== 'text';
      if (stap.type === 'text') {
        const invoer = this.deel('tekst-invoer');
        this.deel('tekst-label').textContent = stap.field.label;
        invoer.value = this.antwoorden[stap.id] || '';
        invoer.placeholder = stap.field.placeholder || '';
        invoer.autocomplete = stap.field.autocomplete || 'off';
        invoer.inputMode = stap.field.inputmode || 'text';
        if (stap.field.maxlength) invoer.maxLength = stap.field.maxlength;
      }

      this.deel('terug').hidden = this.index === 0;
      this.deel('volgende').querySelector('span').textContent =
        this.index === this.stappen.length - 1 ? this.teksten.naar_overzicht : this.teksten.volgende;
      this.toonFout('');
    }

    maakOptie(stap, optie) {
      const kaart = this.sjabloon(stap.type === 'multi' ? 'checkbox' : 'radio');
      const invoer = kaart.querySelector('input');
      const antwoord = this.antwoorden[stap.id];

      invoer.name = stap.id;
      invoer.value = optie.value;
      invoer.checked = Array.isArray(antwoord) ? antwoord.includes(optie.value) : antwoord === optie.value;

      kaart.querySelector('[data-slot="radio-card-title"]').textContent = optie.label;
      const omschrijving = kaart.querySelector('[data-slot="radio-card-description"]');
      omschrijving.textContent = optie.description || '';
      omschrijving.hidden = !optie.description;

      const icoon = this.querySelector(`template[data-kh-icoon="${optie.icon}"]`);
      const vlak = kaart.querySelector('[data-slot="radio-card-icon"]');
      if (icoon) vlak.replaceChildren(icoon.content.cloneNode(true));
      else vlak.remove();

      return kaart;
    }

    renderOverzicht() {
      const rijen = this.stappen.map((stap, i) => {
        const rij = this.sjabloon('overzicht-rij');
        rij.querySelector('[data-kh-veld="vraag"]').textContent = stap.title;
        rij.querySelector('[data-kh-veld="antwoord"]').textContent = this.antwoordTekst(stap);
        const knop = rij.querySelector('[data-index]');
        knop.dataset.index = i;
        // Zeven keer "Wijzig" is voor een screenreader niet te onderscheiden.
        knop.setAttribute('aria-label', `${this.teksten.wijzig}: ${stap.title}`);
        return rij;
      });
      this.deel('lijst').replaceChildren(...rijen);
    }

    antwoordTekst(stap) {
      const antwoord = this.antwoorden[stap.id];
      const leeg = Array.isArray(antwoord) ? antwoord.length === 0 : !antwoord;
      if (leeg) return this.teksten.niet_ingevuld;
      if (stap.type === 'text') return antwoord;
      return []
        .concat(antwoord)
        .map((waarde) => stap.options.find((o) => o.value === waarde)?.label || waarde)
        .join(', ');
    }

    renderResultaat() {
      const { route, regel } = bepaalRoute(this.config, this.antwoorden);
      this.querySelectorAll('[data-kh-route]').forEach((blok) => {
        blok.hidden = blok.dataset.khRoute !== route;
      });

      const reden = this.querySelector(`[data-kh-route="${route}"] [data-kh="reden"]`);
      if (reden) {
        reden.textContent = regel?.reason || '';
        reden.hidden = !regel?.reason;
      }

      const catalogus = catalogusUrl(this.config, this.antwoorden, this.dataset.catalogusUrl);
      this.querySelectorAll('[data-kh="catalogus-link"]').forEach((link) => (link.href = catalogus));
    }

    /* Stappenbalk: één segment per onderdeel (config.groups). Een segment is
       'gedaan', 'actief' of 'open'; binnen het actieve onderdeel loopt de
       vulling mee met de stappen die al beantwoord zijn. */
    renderVoortgang() {
      const stappen = this.stappen;
      const groepen = this.config.groups || [];
      const balk = this.deel('stappenbalk');
      const klaar = this.fase === 'vragen' ? this.index : stappen.length;
      const huidig = this.fase === 'vragen' ? stappen[this.index] : null;

      if (balk.childElementCount !== groepen.length) {
        balk.replaceChildren(...groepen.map(() => this.sjabloon('groep')));
      }

      groepen.forEach((groep, i) => {
        const inGroep = stappen.filter((stap) => stap.groep === groep.id);
        const gedaan = inGroep.filter((stap) => stappen.indexOf(stap) < klaar).length;
        const deel = inGroep.length ? gedaan / inGroep.length : 1;
        const actief = huidig ? huidig.groep === groep.id : false;

        const el = balk.children[i];
        el.querySelector('[data-kh-veld="label"]').textContent = groep.label;
        el.querySelector('[data-kh-veld="vulling"]').style.width = `${Math.round(deel * 100)}%`;
        el.dataset.staat = deel >= 1 ? 'gedaan' : actief ? 'actief' : 'open';
        if (actief) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });

      const label =
        this.fase === 'vragen'
          ? this.teksten.stap_van.replace('{huidig}', this.index + 1).replace('{totaal}', stappen.length)
          : this.teksten.overzicht;

      this.deel('teller').textContent = label;
      this.deel('live').textContent = label;
    }

    /* Uitlegpaneel links: het paneel van de huidige stap, anders het paneel
       dat de rest opvangt. Video's pauzeren zodra hun paneel verborgen is,
       zodat er nooit geluid of beweging is van een stap die niet in beeld is. */
    renderPaneel() {
      const huidig = this.fase === 'vragen' ? this.stappen[this.index] : null;
      const panelen = [...this.querySelectorAll('[data-kh-paneel]')];
      if (!panelen.length) return;

      const eigen = huidig && panelen.find((el) => el.dataset.khPaneel === huidig.id);
      const zichtbaar = eigen || panelen.find((el) => el.dataset.khPaneel === 'standaard');

      panelen.forEach((el) => {
        const toon = el === zichtbaar;
        el.hidden = !toon;
        const video = el.querySelector('video');
        if (!video) return;
        if (toon) video.play().catch(() => {});
        else video.pause();
      });

      if (zichtbaar && zichtbaar.dataset.khPaneel === 'standaard') {
        const tekst = this.deel('paneel-tekst');
        const hulp = this.deel('paneel-hulp');
        tekst.textContent = huidig ? huidig.intro || '' : '';
        hulp.textContent = huidig ? huidig.help || '' : '';
        hulp.hidden = !hulp.textContent;
      }
    }

    /** Herstart de shadcn-animatie (data-open:animate-in) bij elke wissel. */
    speelAnimatie() {
      const paneel = this.deel(this.fase);
      paneel.removeAttribute('data-state');
      void paneel.offsetWidth;
      paneel.setAttribute('data-state', 'open');
    }

    toonFout(bericht) {
      const fout = this.deel('fout');
      fout.textContent = bericht;
      fout.hidden = !bericht;
    }
  }

  customElements.define('flux-keuzehulp', FluxKeuzehulp);
})();
